export type FilesState = {
  settings: { gatewayUrl?: string; token?: string };
  filesLoading: boolean;
  filesList: Array<{ name: string; size: number; mime_type: string; source: string }>;
  filesError: string | null;
};

function baseUrl(state: FilesState): string {
  return (state.settings.gatewayUrl?.replace(/^ws/, "http") ?? "").replace(/\/ws\/?$/, "");
}

function authHeaders(state: FilesState): Record<string, string> {
  return { Authorization: `Bearer ${state.settings.token ?? ""}` };
}

export async function loadFiles(state: FilesState): Promise<void> {
  state.filesLoading = true;
  state.filesError = null;
  try {
    const resp = await fetch(`${baseUrl(state)}/api/files`, {
      headers: authHeaders(state),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: resp.statusText }));
      state.filesError = (err as { error?: string }).error ?? "Failed to load files";
      return;
    }
    const data = await resp.json();
    state.filesList = data.files ?? [];
  } catch (err) {
    state.filesError = `Failed to load files: ${String(err)}`;
  } finally {
    state.filesLoading = false;
  }
}

export async function downloadFile(state: FilesState, filename: string): Promise<void> {
  try {
    const resp = await fetch(
      `${baseUrl(state)}/api/files/${encodeURIComponent(filename)}/download`,
      { headers: authHeaders(state) },
    );
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: resp.statusText }));
      state.filesError = (err as { error?: string }).error ?? "Download failed";
      return;
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    state.filesError = `Download failed: ${String(err)}`;
  }
}

export async function deleteFile(state: FilesState, filename: string): Promise<void> {
  try {
    const resp = await fetch(
      `${baseUrl(state)}/api/files/${encodeURIComponent(filename)}`,
      { method: "DELETE", headers: authHeaders(state) },
    );
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: resp.statusText }));
      state.filesError = (err as { error?: string }).error ?? "Delete failed";
      return;
    }
    // Reload file list
    await loadFiles(state);
  } catch (err) {
    state.filesError = `Delete failed: ${String(err)}`;
  }
}
